import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { GridSkeleton } from "@/components/Loaders";
import { Star, Check, ExternalLink, Boxes, Search } from "lucide-react";

export default function Resources() {
  const [params, setParams] = useSearchParams();
  const [tools, setTools] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const category = params.get("category") || "all";

  const fetch = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (category !== "all") q.set("category", category);
    if (search) q.set("search", search);
    const res = await api.get(`/tools?${q}`);
    setTools(res.data); setLoading(false);
  }, [category, search]);

  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => { api.get("/tool-categories").then((r) => setCats(r.data)); }, []);

  const setCat = (c) => { const p = new URLSearchParams(params); if (c === "all") p.delete("category"); else p.set("category", c); setParams(p); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-50 flex items-center gap-3"><Boxes className="w-8 h-8 text-cyan-400" />Builder Resource Marketplace</h1>
        <p className="text-slate-400 mt-1">The tools, APIs and platforms to build and launch faster.</p>
      </div>

      <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 mb-5 max-w-md focus-within:border-cyan-500/50">
        <Search className="w-4 h-4 text-slate-500" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tools…" className="bg-transparent text-sm text-slate-200 outline-none flex-1" data-testid="resources-search-input" />
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        <button onClick={() => setCat("all")} className={`px-3 py-1.5 rounded-lg text-sm border ${category === "all" ? "border-cyan-500 bg-cyan-500/15 text-cyan-200" : "border-white/10 text-slate-400"}`}>All</button>
        {cats.map((c) => <button key={c} onClick={() => setCat(c)} className={`px-3 py-1.5 rounded-lg text-sm border ${category === c ? "border-cyan-500 bg-cyan-500/15 text-cyan-200" : "border-white/10 text-slate-400"}`}>{c}</button>)}
      </div>

      {loading ? <GridSkeleton /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tools.map((t) => (
            <div key={t.name} className="glass rounded-xl p-5 card-hover" data-testid={`tool-card-${t.name.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-semibold text-slate-100">{t.name}</h3>
                <span className="flex items-center gap-1 text-sm font-mono text-amber-400"><Star className="w-3.5 h-3.5" fill="currentColor" />{t.rating}</span>
              </div>
              <span className="text-[11px] font-mono uppercase text-cyan-400">{t.category}</span>
              <p className="text-sm text-slate-400 mt-2 mb-4">{t.description}</p>
              <div className="space-y-1.5 text-xs text-slate-500 mb-4">
                <p><span className="text-slate-400">Pricing:</span> {t.pricing}</p>
                <p><span className="text-slate-400">Best for:</span> {t.best_for}</p>
                <p className="flex items-center gap-1">{t.free_plan ? <><Check className="w-3 h-3 text-emerald-400" />Free plan available</> : "No free plan"}</p>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-4">{(t.alternatives || []).map((a) => <span key={a} className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 border border-white/8 text-slate-400">alt: {a}</span>)}</div>
              <a href={t.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-cyan-400 hover:gap-2 transition-all">Visit <ExternalLink className="w-3.5 h-3.5" /></a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
